import './Main.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { useModal } from '@/components/modal/Modal'
import { SuperAdminInsertMachineTypePopup } from '@/pages/for/superadmin/machinetype/MachineType'
import { SuperAdminInsertUserGroupPopup } from '@/pages/for/superadmin/usergroups/groups'
import { WizardData } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM } from '@/common/time'
import { fetchPDF } from '@/common/fetch'

export interface UserRange {
  onFireRatio: number;
  totalActive: number;
  totalBuckets: number;
  totalOnFire: number;
  userId: string;
  username: string;
}

const tenMinInSec = 60 * 10

export const UsersReportWizardResult = () => {
  const { services, session, translates } = useAppContext()
  const { wizardData, updateWizardData, nextStep } = useWizard<WizardData>()
  const [data, setData] = useState<UserRange[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })

  function refresh() {
    services.session.fetchWithToken(`/api/communal/my/reports/usersranking?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}`).then((response) => {
      return response.json()
    }).then((data: UserRange[]) => {
      data ||= []
      setData(data.sort((a, b) => b.onFireRatio - a.onFireRatio))
    })
  }
  
  function loadPDF() {
    fetchPDF({
      url: `/api/communal/my/reports/usersranking/pdf?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}&lang=${session?.user.lang}`,
      fileName: "usersranking.pdf",
      services
    })
  }

  useEffect(refresh, [])

  const top5 = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => b.onFireRatio - a.onFireRatio).slice(0, 5);
  }, [data]);

  const bottom5 = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => a.onFireRatio - b.onFireRatio).slice(0, 5);
  }, [data]);

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }


  function headerRow(key: string, transKey?: string) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol'>
      {translates.for('report.usersranking.summary.table.' + transKey)}
    </div>
  }

  const renderTable = (
    data: UserRange[], 
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined
  ) => {
    return <StaticPaginatedTable<UserRange>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('username', 'username')}
        {headerRow('onFireRatio', 'ratio')}
        {headerRow('activeTime', 'active_time')}
      </>}
      row={(row) => {
        const openDetails = () => {
          const url = `${window.location.origin}/communal/statistics/users?length=1d&user=${row.username}`;
          window.open(url, '_blank');
        }
        return <>
          <div className={`col`} onClick={openDetails}>{row.username}</div>
          <div className={`col`} onClick={openDetails}>{row.onFireRatio}</div>
          <div className={`col`} onClick={openDetails}>
            <TimeDHM dhm={secondsToDHM(row.totalActive * tenMinInSec)} translates={translates} />
          </div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.usersranking.summary.title')}</h2>

      <h3>{translates.for('report.usersranking.summary.top_title')}</h3>
      {renderTable(top5, { pageNumber: 1, pageSize: 5 }, undefined)}

      <h3>{translates.for('report.usersranking.summary.bottom_title')}</h3>
      {renderTable(bottom5, { pageNumber: 1, pageSize: 5 }, undefined)}

      <h3>{translates.for('report.usersranking.summary.all_title')}</h3>
      {renderTable(data, pagginationState, setPagginationState)}

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={loadPDF}>{translates.for('report.usersranking.wizard.gen_pdf')}</button>
      </div>
    </div>
  </>
}